import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import "./ImportModal.css";

const ImportModal = ({
  isOpen,
  onClose,
  onImport,
  availableFields,
  columns,
  showToast,
  existingLeads = [],
  users = [], 
}) => {
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [duplicates, setDuplicates] = useState([]); 
  const [duplicateActions, setDuplicateActions] = useState({}); 
  const [finalData, setFinalData] = useState(null); 
  const [selectedUser, setSelectedUser] = useState("");
  const [primaryUsers, setPrimaryUsers] = useState([]);
  const [secondaryUsers, setSecondaryUsers] = useState([]);
  
  useEffect(() => {
    if (!isOpen) {
      setFileData([]);
      setHeaders([]);
      setMapping({});
      setDuplicates([]);
      setDuplicateActions({});
      setFinalData(null);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      if (jsonData.length) {
        const fileHeaders = jsonData[0].map(header => header.trim());
        console.log("File Headers:", fileHeaders);
        setHeaders(fileHeaders);
        setFileData(jsonData.slice(1));
        const fields =
          availableFields && availableFields.length > 0
            ? availableFields
            : columns
            ? columns.map((col) => col.Header)
            : [];
        const defaultMapping = {};
        fileHeaders.forEach((header) => {
          const match = fields.find(
            (field) => field.toLowerCase() === header.toLowerCase()
          );
          defaultMapping[header] = match || "";
        });
        console.log("Default Mapping:", defaultMapping);
        setMapping(defaultMapping);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMappingChange = (fileHeader, newField) => {
    console.log(`Mapping change - File header: ${fileHeader}, New field: ${newField}`);
    setMapping((prevMapping) => ({
      ...prevMapping,
      [fileHeader]: newField,
    }));
  };

  const transformData = () => {
    const transformedData = fileData.map((row) => {
      const rowObj = {};
      headers.forEach((header, index) => {
        const trimmedHeader = header.trim();
        const targetField = mapping[trimmedHeader];
        if (targetField) {
          const cellValue = row[index];
          if (trimmedHeader.toLowerCase() === "phone") {
            console.log(`Raw value for phone:`, cellValue);
          }
          rowObj[targetField] = cellValue !== undefined ? cellValue : "";
        }
      });
      if (!rowObj.assigned_to && selectedUser) {
        rowObj.assigned_to = selectedUser;
      }
      
      // Add assignment data for new system
      rowObj._assignments = {
        primary: primaryUsers,
        secondary: secondaryUsers
      };
      
      return rowObj;
    });
    console.log("Transformed Data (rows to be inserted):", transformedData);
    return transformedData;
  };
  
  

  const checkDuplicates = (data) => {
    console.log("Existing leads in table:", existingLeads);
    const existingEmails = existingLeads.map(lead => lead.email);
    console.log("Existing Emails:", existingEmails);

    const dupes = [];
    data.forEach((row, index) => {
      console.log(`Checking new row index ${index}:`, row);
      if (row.email && row.name && row.phone) {
        existingLeads.forEach((lead) => {
          console.log(
            `Comparing new row email (${row.email}) with existing email (${lead.email})`
          );
          if (
            lead.email &&
            lead.name &&
            lead.phone &&
            lead.email.toLowerCase() === row.email.toLowerCase() &&
            lead.name.toLowerCase() === row.name.toLowerCase() &&
            lead.phone.toLowerCase() === row.phone.toLowerCase()
          ) {
            let differences = false;
            for (const key in row) {
              if (row.hasOwnProperty(key)) {
                const newValue = row[key] || "";
                const existingValue = lead[key] || "";
                if (
                  String(newValue).trim().toLowerCase() !==
                  String(existingValue).trim().toLowerCase()
                ) {
                  differences = true;
                  break;
                }
              }
            }
            console.log(
              `Found duplicate for row ${index} with existing lead id ${lead.id}. Differences found: ${differences}`
            );
            dupes.push({ index, existingId: lead.id, row, differences });
          }
        });
      }
    });
    console.log("Duplicates Found:", dupes);
    return dupes;
  };

  const handleSubmit = () => {
    console.log("Handle Submit triggered");
    console.log("Current table data (existing leads):", existingLeads);
    const transformed = transformData();
    const dupes = checkDuplicates(transformed);

    if (dupes.length > 0) {
      const defaultActions = {};
      dupes.forEach(({ index, differences }) => {
        defaultActions[index] = differences ? "update" : "skip";
      });
      console.log("Default Duplicate Actions:", defaultActions);
      setDuplicateActions(defaultActions);
      setDuplicates(dupes);
      setFinalData(null);
      console.log("Duplicate review required. Waiting for user action on duplicate rows.");
    } else {
      console.log("No duplicates found. Proceeding with import.");
      onImport(transformed);
      onClose();
    }
  };

  const handleFinalSubmit = () => {
    console.log("Finalizing import with duplicate actions:", duplicateActions);
    const transformed = transformData();
  
    const finalImportData = transformed.reduce((acc, row, idx) => {
      if (duplicateActions.hasOwnProperty(idx)) {
        const action = duplicateActions[idx];
        if (action === "skip") {
          console.log(`Skipping row ${idx} as per user action.`);
          return acc;
        } else if (action === "update" || action === "insert") {
          const { _duplicateAction, ...rest } = row;
          acc.push(rest);
          return acc;
        }
      } else {
        acc.push(row);
        return acc;
      }
    }, []);
  
    const totalRows = transformed.length;
    const skippedCount = transformed.reduce((count, row, idx) => {
      return duplicateActions.hasOwnProperty(idx) && duplicateActions[idx] === "skip"
        ? count + 1
        : count;
    }, 0);
    const insertedCount = totalRows - skippedCount;
  
    console.log("Final Import Data (after duplicate review):", finalImportData);
  
    if (typeof showToast === "function") {
      showToast(
        `Import completed: ${skippedCount} row${skippedCount !== 1 ? "s" : ""} skipped, ${insertedCount} row${insertedCount !== 1 ? "s" : ""} inserted.`,
        "info"
      );
    }
  
    if (finalImportData.length === 0) {
      console.log("No new rows to import after duplicate review.");
      onClose();
      return;
    }
  
    onImport(finalImportData);
    onClose();
  };
  

  const handleDuplicateActionChange = (index, action) => {
    console.log(`Duplicate action change - Row index: ${index}, Action: ${action}`);
    setDuplicateActions((prev) => ({ ...prev, [index]: action }));
  };

  return (
    <>
      {isOpen && (
        <div className="import-modal-overlay">
          <div className="import-modal">
            <h3>Import Leads</h3>
            <button className="close-button" onClick={onClose}>
              ×
            </button>
            <div>
              <input type="file" accept=".xlsx, .xls" onChange={handleFileChange} />
            </div>

            {headers.length > 0 && (
              <div className="mapping-container">
                <div className="assignment-container">
                  <label htmlFor="assignUser">Assign imported rows to (Legacy):</label>
                  <select
                    id="assignUser"
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                  >
                    <option value="">-- Select User --</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="assignment-container">
                  <label>Primary Users (Multiple):</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
                    {primaryUsers.map((userId) => {
                      const user = users.find(u => u.id === parseInt(userId));
                      if (!user) return null;
                      return (
                        <div
                          key={userId}
                          style={{
                            backgroundColor: user.chip_color || "#e0e0e0",
                            padding: "4px 10px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                          }}
                        >
                          <span>{user.first_name} {user.last_name}</span>
                          <button
                            type="button"
                            onClick={() => setPrimaryUsers(primaryUsers.filter(id => id !== userId))}
                            style={{
                              background: "rgba(0,0,0,0.2)",
                              border: "none",
                              borderRadius: "50%",
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                              fontSize: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !primaryUsers.includes(e.target.value)) {
                        setPrimaryUsers([...primaryUsers, e.target.value]);
                      }
                    }}
                    style={{ width: "100%" }}
                  >
                    <option value="">-- Add Primary User --</option>
                    {users.filter(u => !primaryUsers.includes(u.id.toString())).map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="assignment-container">
                  <label>Secondary Users (Multiple):</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
                    {secondaryUsers.map((userId) => {
                      const user = users.find(u => u.id === parseInt(userId));
                      if (!user) return null;
                      return (
                        <div
                          key={userId}
                          style={{
                            backgroundColor: user.chip_color || "#e0e0e0",
                            padding: "4px 10px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                          }}
                        >
                          <span>{user.first_name} {user.last_name}</span>
                          <button
                            type="button"
                            onClick={() => setSecondaryUsers(secondaryUsers.filter(id => id !== userId))}
                            style={{
                              background: "rgba(0,0,0,0.2)",
                              border: "none",
                              borderRadius: "50%",
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                              fontSize: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !secondaryUsers.includes(e.target.value)) {
                        setSecondaryUsers([...secondaryUsers, e.target.value]);
                      }
                    }}
                    style={{ width: "100%" }}
                  >
                    <option value="">-- Add Secondary User --</option>
                    {users.filter(u => !secondaryUsers.includes(u.id.toString())).map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <h4>Map File Columns to Database Fields</h4>
                {headers.map((header) => {
                  const usedFields = Object.entries(mapping)
                    .filter(([key, value]) => key !== header && value)
                    .map(([_, value]) => value);
                  return (
                    <div key={header} className="mapping-row">
                      <span className="file-header">{header}</span>
                      <select
                        value={mapping[header]}
                        onChange={(e) =>
                          handleMappingChange(header, e.target.value)
                        }
                        className={mapping[header] ? "matched" : "unmatched"}
                      >
                        <option value="">-- Select Field --</option>
                        {(availableFields && availableFields.length > 0
                          ? availableFields
                          : columns
                          ? columns.map((col) => col.Header)
                          : []
                        ).map((field) => (
                          <option
                            key={field}
                            value={field}
                            disabled={usedFields.includes(field)}
                          >
                            {field}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
            {headers.length > 0 && duplicates.length === 0 && (
              <button className="submit-button" onClick={handleSubmit}>
                Import Data
              </button>
            )}
            {duplicates.length > 0 && (
              <div className="duplicate-review">
                <h4>Duplicate Records Found</h4>
                <p>
                  For rows detected as duplicates (based on email, name, and phone), choose an action:
                  <br />
                  <strong>Skip</strong> (ignore) or <strong>Insert as New</strong> (create a new record anyway).
                </p>
                {duplicates.map(({ index, existingId, row, differences }) => (
                  <div key={index} className="duplicate-row">
                    <span>
                      Row {index + 1} (Email: {row.email}, Name: {row.name}, Phone: {row.phone}){" "}
                      {differences ? "(Differences found)" : "(No differences)"}
                    </span>
                    <select
                      value={duplicateActions[index] || (differences ? "update" : "skip")}
                      onChange={(e) =>
                        handleDuplicateActionChange(index, e.target.value)
                      }
                    >
                      <option value="skip">Skip</option>
                      <option value="insert">Insert as New</option>
                    </select>
                  </div>
                ))}
                <button className="submit-button" onClick={handleFinalSubmit}>
                  Finalize Import
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
  
};

export default ImportModal;
